import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldListDto } from '@carbonwebmail/contracts'
import { IMPORT_BATCH_SIZE } from '@carbonwebmail/contracts'
import type { ImportContactsResult } from '@carbonwebmail/domain'
import { absorbImport, emptyImportTally, finishedImport } from '@carbonwebmail/domain'
import Papa from 'papaparse'
import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useLoaded } from '../../ui/useLoaded.js'
import { useSegmentList } from '../segments/useSegmentList.js'

/**
 * The import, from a file on disk to rows in the database.
 *
 * The file is parsed in the browser and sent in batches. Not uploaded whole:
 * a hundred megabytes in one request times out, spikes memory on the server,
 * and can show no progress while it happens. Batches give all three back, and
 * make a failure cheap — one batch is retried, not the file.
 *
 * CSV is parsed by papaparse rather than by splitting on commas. A quoted field
 * containing a comma, a newline inside a value, a BOM at the start of the file:
 * each is ordinary in exports from real systems and each breaks a hand-rolled
 * parser in a way that silently corrupts a name or an address.
 */

/** The segment an import connects its rows to, as the url names it. */
export const INTO_SEGMENT = 'segment'

export type ImportStep = 'upload' | 'map' | 'preview' | 'importing' | 'done'

export interface ParsedFile {
  readonly name: string
  readonly headers: readonly string[]
  readonly rows: readonly (readonly string[])[]
}

/** Which CSV column feeds which field. `null` means "do not import". */
export interface Mapping {
  readonly email: number | null
  readonly name: number | null
  /**
   * A column index per declared field key.
   *
   * Keyed by field key rather than held as a list, because the question the
   * screen asks is "where does Cohort come from" — once per field, answered
   * or not. A list would let one field be mapped twice.
   */
  readonly custom: Readonly<Record<string, number | null>>
}

export interface ImportProgress {
  readonly sent: number
  readonly total: number
}

export type ImportState =
  | { readonly step: 'upload'; readonly problem: string | undefined }
  | { readonly step: 'map'; readonly file: ParsedFile; readonly mapping: Mapping }
  | { readonly step: 'preview'; readonly file: ParsedFile; readonly mapping: Mapping }
  | { readonly step: 'importing'; readonly progress: ImportProgress }
  | { readonly step: 'done'; readonly result: ImportContactsResult; readonly file: string }

/**
 * A header that looks like an address column, so the common case needs no
 * mapping at all. Deliberately a guess the person can override — never a
 * decision made for them.
 */
function guess(headers: readonly string[], candidates: readonly string[]): number | null {
  const index = headers.findIndex((header) =>
    candidates.some((candidate) => header.trim().toLowerCase() === candidate),
  )
  return index === -1 ? null : index
}

export function useImport({ client }: { readonly client: ApiClient }) {
  const [state, setState] = useState<ImportState>({ step: 'upload', problem: undefined })
  /**
   * A segment every imported contact is connected to.
   *
   * **Chosen here, and the url only says what it starts as.** A segment's own
   * menu sends people here with one already picked, because "import contacts
   * into this segment" is a thing somebody wants to do from the segment; but
   * arriving from the board — or from the sidebar — they have to be able to say
   * which one, and until 2026-09-19 they could not. The url is read once, as the
   * initial value, so a refresh keeps what the link said and a change made here
   * is not fighting it.
   *
   * It travels with the rows, so the contact and its membership are one write.
   */
  const [params] = useSearchParams()
  const [into, setInto] = useState<string | undefined>(params.get(INTO_SEGMENT) ?? undefined)

  /**
   * The segments that can be chosen.
   *
   * A picker rather than a free text box: a segment is a thing that exists, and
   * typing a name that matches none would silently connect nobody. A failure
   * loading them leaves the import working with no segment chosen, which is the
   * screen as it was before this existed.
   */
  const segments = useSegmentList({ client })
  const connectable = segments.state.status === 'ready' ? segments.state.value.segments : []

  /**
   * The fields this organisation declared, so a column can be mapped to one.
   *
   * A failure here is not fatal — email and name still import — so it is not
   * shown as an error on the upload step. What it costs is the custom columns,
   * and the map step says so rather than silently offering fewer fields.
   */
  const loadFields = useCallback(() => client.declaredFields('contacts'), [client])
  const { state: fields } = useLoaded<ContactFieldListDto>(
    loadFields,
    'We could not load your contact fields, so only the email and name columns can be imported.',
  )
  /**
   * Memoised because the parse callback closes over it.
   *
   * A fresh array each render would rebuild that callback each render; not
   * depending on it at all would capture the empty list from before the
   * request came back, and a file chosen in that moment would auto-map
   * nothing with no sign of why.
   */
  const declared = useMemo(() => (fields.status === 'ready' ? fields.value.fields : []), [fields])

  const choose = useCallback(
    (file: File) => {
      Papa.parse<string[]>(file, {
        skipEmptyLines: 'greedy',
        complete: (results) => {
          const [headers, ...rows] = results.data
          if (headers === undefined || headers.length === 0) {
            setState({ step: 'upload', problem: 'That file has no header row.' })
            return
          }
          if (rows.length === 0) {
            setState({ step: 'upload', problem: 'That file has a header but no rows.' })
            return
          }

          setState({
            step: 'map',
            file: { name: file.name, headers, rows },
            mapping: {
              email: guess(headers, ['email', 'email address', 'e-mail', 'mail']),
              name: guess(headers, ['name', 'full name', 'contact', 'contact name']),
              // A column headed "Cohort" fills the field labelled Cohort, and
              // one headed `cohort` fills the field keyed that. Both are what
              // somebody exporting from another system would have.
              custom: Object.fromEntries(
                declared.map((field) => [
                  field.key,
                  guess(headers, [field.label.toLowerCase(), field.key]),
                ]),
              ),
            },
          })
        },
        error: () => {
          setState({ step: 'upload', problem: 'We could not read that file.' })
        },
      })
    },
    [declared],
  )

  const setMapping = useCallback((mapping: Mapping) => {
    setState((current) =>
      current.step === 'map' || current.step === 'preview' ? { ...current, mapping } : current,
    )
  }, [])

  const goto = useCallback((step: 'upload' | 'map' | 'preview') => {
    setState((current) => {
      if (step === 'upload') return { step: 'upload', problem: undefined }
      if (current.step !== 'map' && current.step !== 'preview') return current
      return { step, file: current.file, mapping: current.mapping }
    })
  }, [])

  /**
   * Sends the file, one batch at a time, accumulating the counts.
   *
   * Batches run in sequence rather than in parallel. The database work is one
   * statement per batch and the bottleneck is not the round trip; running
   * twenty at once would multiply the load on a shared table for no gain a
   * person would notice.
   */
  const start = useCallback(
    async (file: ParsedFile, mapping: Mapping) => {
      // The caller is on a screen that already holds both, so they are passed in
      // rather than read back out of state. A callback that reached into state
      // would either list it as a dependency and be rebuilt on every keystroke,
      // or capture the first render's copy and import the wrong file.
      if (mapping.email === null) return

      const emailColumn = mapping.email
      const nameColumn = mapping.name

      // Resolved once rather than per row: a forty-thousand-row file would
      // otherwise walk the mapping forty thousand times to learn the same thing.
      const customColumns = Object.entries(mapping.custom).filter(
        (entry): entry is [string, number] => entry[1] !== null,
      )

      const rows = file.rows.map((row) => ({
        email: row[emailColumn] ?? '',
        name: nameColumn === null ? '' : (row[nameColumn] ?? ''),
        custom: Object.fromEntries(customColumns.map(([key, column]) => [key, row[column] ?? ''])),
      }))

      const tally = emptyImportTally()

      setState({ step: 'importing', progress: { sent: 0, total: rows.length } })

      for (let offset = 0; offset < rows.length; offset += IMPORT_BATCH_SIZE) {
        const batch = rows.slice(offset, offset + IMPORT_BATCH_SIZE)
        try {
          absorbImport(
            tally,
            await client.importContacts({
              source: file.name,
              rows: batch,
              // Connected in the same transaction as the rows, and only for the
              // ones actually inserted — an address already on the list keeps
              // the segments it has, for the same reason it keeps its state.
              ...(into === undefined ? {} : { segmentIds: [into] }),
            }),
            offset,
          )
        } catch {
          // Stops rather than carrying on. Continuing past a failed batch would
          // report a total that silently excludes rows nobody was told about.
          tally.rejected.push({
            line: offset + 2,
            reason: 'The import stopped here: the server could not be reached.',
          })
          setState({ step: 'done', file: file.name, result: finishedImport(tally) })
          return
        }
        setState({
          step: 'importing',
          progress: { sent: Math.min(offset + IMPORT_BATCH_SIZE, rows.length), total: rows.length },
        })
      }

      setState({ step: 'done', file: file.name, result: finishedImport(tally) })
    },
    [client, into],
  )

  return { state, declared, choose, setMapping, goto, start, into, setInto, connectable }
}
